import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAdminMangaList, usePublishManga } from '../api/useAdmin'
import { mediaUrl } from '../utils/media'
import { Skeleton } from '../components/ui/Skeleton'
import { STATUS_CSS } from '../utils/constants'
import { useMeta } from '../api/useManga'
import Pagination from '../components/ui/Pagination'
import toast from 'react-hot-toast'

const EMPTY_FILTERS = { status: '', type: '', is_published: '', is_mangadex: '' }

export default function Admin() {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const { data: meta } = useMeta()
  const { mutate: publishManga } = usePublishManga()

  const activeCount = Object.values(filters).filter(Boolean).length

  const apiFilters = {
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.type ? { type: filters.type } : {}),
    ...(filters.is_published !== '' ? { is_published: filters.is_published === 'true' } : {}),
    ...(filters.is_mangadex !== '' ? { is_mangadex: filters.is_mangadex === 'true' } : {}),
  }

  const { data, isLoading } = useAdminMangaList(page, apiFilters)
  const mangaList = data?.items ?? []
  const totalPages = data?.pages ?? 1

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }))
    setPage(1)
  }

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS)
    setQ('')
    setPage(1)
  }

  const handlePublish = (manga) => {
    publishManga(manga.id, {
      onSuccess: () => toast.success(manga.is_published ? 'Снято с публикации' : 'Опубликовано'),
      onError: () => toast.error('Ошибка'),
    })
  }

  const selectCls = 'px-3 py-2 rounded-lg text-sm outline-none bg-surface2 border border-line text-fg focus:border-accent transition-colors'

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold">Панель администратора</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/admin/users"
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all bg-surface border border-line text-fg hover:border-accent">
            Пользователи
          </Link>
          <Link to="/admin/reports"
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all bg-surface border border-line text-fg hover:border-accent">
            Жалобы
          </Link>
          <Link to="/admin/support"
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all bg-surface border border-line text-fg hover:border-accent">
            Поддержка
          </Link>
          <Link to="/admin/lookups"
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all bg-surface border border-line text-fg hover:border-accent">
            Справочники
          </Link>
          <Link to="/admin/manga/new"
            className="px-4 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90 bg-accent text-on-accent">
            + Добавить мангу
          </Link>
        </div>
      </div>

      {/* Search + filter toggle */}
      <div className="flex gap-2 mb-3">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1) }}
          placeholder="Поиск по названию..."
          className="flex-1 px-4 py-2 rounded-xl text-sm outline-none bg-surface border border-line text-fg focus:border-accent transition-colors placeholder:text-muted"
        />
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-all
            ${filtersOpen || activeCount > 0
              ? 'bg-accent/10 border-accent text-accent'
              : 'bg-surface border-line text-muted hover:border-accent hover:text-fg'
            }`}
        >
          Фильтры
          {activeCount > 0 && (
            <span className="w-4 h-4 rounded-full text-xs flex items-center justify-center bg-accent text-on-accent">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* Collapsible filter block */}
      {filtersOpen && (
        <div className="mb-5 p-4 rounded-xl bg-surface border border-line flex flex-col gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted">Публикация</label>
              <select value={filters.is_published} onChange={(e) => setFilter('is_published', e.target.value)} className={selectCls}>
                <option value="">Все</option>
                <option value="true">Опубликовано</option>
                <option value="false">Скрыто</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted">Статус</label>
              <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className={selectCls}>
                <option value="">Любой</option>
                {(meta?.statuses ?? []).map((s) => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted">Тип</label>
              <select value={filters.type} onChange={(e) => setFilter('type', e.target.value)} className={selectCls}>
                <option value="">Любой</option>
                {(meta?.types ?? []).map((t) => (
                  <option key={t.code} value={t.code}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted">Источник</label>
              <select value={filters.is_mangadex} onChange={(e) => setFilter('is_mangadex', e.target.value)} className={selectCls}>
                <option value="">Все</option>
                <option value="false">Admin</option>
                <option value="true">MangaDex</option>
              </select>
            </div>
          </div>
          {activeCount > 0 && (
            <button onClick={resetFilters} className="self-start text-xs text-muted hover:text-red-400 transition-colors">
              Сбросить фильтры
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
        </div>
      ) : !mangaList?.length ? (
        <div className="py-24 text-center text-muted">
          {activeCount > 0 || q ? 'Ничего не найдено.' : 'Манги пока нет.'}{' '}
          {!activeCount && !q && <Link to="/admin/manga/new" className="text-accent">Добавить первую</Link>}
        </div>
      ) : (
        <>
          {data?.total > 0 && (
            <p className="text-sm text-muted mb-3">Найдено: {data.total}</p>
          )}
          <div className="flex flex-col gap-2">
            {mangaList.map((manga) => (
              <div key={manga.id} className="flex items-center gap-4 px-4 py-3 rounded-xl bg-surface border border-line">
                <Link to={`/admin/manga/${manga.id}`} className="shrink-0 rounded-lg overflow-hidden bg-surface2"
                  style={{ width: 36, height: 52 }}>
                  {manga.cover_url
                    ? <img src={mediaUrl(manga.cover_url)} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full" />
                  }
                </Link>

                <Link to={`/admin/manga/${manga.id}`} className="flex-1 min-w-0 group">
                  <p className="text-sm font-semibold truncate transition-colors group-hover:text-accent">
                    {manga.title}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {manga.status && (
                      <span className={`text-xs ${STATUS_CSS[manga.status]}`}>
                        {meta?.statuses?.find(s => s.code === manga.status)?.name ?? manga.status}
                      </span>
                    )}
                    <span className="text-xs text-muted">{manga.chapters_count ?? 0} гл.</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded border
                      ${manga.is_published
                        ? 'border-green-400/30 text-green-400'
                        : 'border-line text-muted'}`}>
                      {manga.is_published ? 'Опубликовано' : 'Скрыто'}
                    </span>
                    {manga.is_mangadex
                      ? <span className="text-xs px-1.5 py-0.5 rounded bg-blue-400/15 text-blue-400">MangaDex</span>
                      : <span className="text-xs px-1.5 py-0.5 rounded bg-purple-400/15 text-purple-400">Admin</span>
                    }
                  </div>
                </Link>

                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => handlePublish(manga)}
                    className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all
                      ${manga.is_published
                        ? 'border-red-400/30 text-red-400 hover:bg-red-400/10'
                        : 'border-green-400/30 text-green-400 hover:bg-green-400/10'
                      }`}>
                    <span className="hidden sm:inline">{manga.is_published ? 'Снять' : 'Опубликовать'}</span>
                    <span className="sm:hidden">{manga.is_published ? '↓' : '↑'}</span>
                  </button>
                  <Link to={`/admin/manga/${manga.id}`}
                    className="hidden sm:block shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold bg-surface2 text-fg hover:bg-surface3 transition-colors">
                    Редактировать
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <Pagination page={page} total={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  )
}
